import { Facebook, Instagram, Youtube } from "lucide-react";
import type { Show } from "@/types/show";

// ============================================================================
// SHARED SOCIAL PLATFORM ICONS & CONFIG
// Used by both SocialMediaConnections (settings) and public ShowPage
// ============================================================================

export type SocialPlatformId =
  | "facebook"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "twitter"
  | "spotify"
  | "apple";

export interface SocialPlatformConfig {
  id: SocialPlatformId;
  name: string;
  displayName: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  urlPattern: RegExp;
  urlPlaceholder: string;
  extractUsername: (url: string) => string | null;
}

// Custom TikTok icon (no lucide equivalent)
export const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Custom X (Twitter) icon
export const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Spotify icon (filled circle, brand-matched in color via className)
export const SpotifyIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.56.3z" />
  </svg>
);

// Apple Podcasts icon
export const ApplePodcastsIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.5c2.485 0 4.5 2.015 4.5 4.5S14.485 13.5 12 13.5 7.5 11.485 7.5 9 9.515 4.5 12 4.5zm-2.4 11.55a8.7 8.7 0 0 1 4.8 0c.66.18 1.05.84.93 1.5l-.6 4.05c-.09.66-.66 1.14-1.32 1.14h-2.82c-.66 0-1.23-.48-1.32-1.14l-.6-4.05c-.12-.66.27-1.32.93-1.5z" />
  </svg>
);


export const SOCIAL_PLATFORMS: SocialPlatformConfig[] = [
  {
    id: "facebook",
    name: "Facebook",
    displayName: "Facebook",
    icon: Facebook,
    color: "text-[#1877F2]",
    urlPattern: /^https?:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9._-]+\/?$/,
    urlPlaceholder: "https://facebook.com/yourpage",
    extractUsername: (url) => {
      const match = url.match(/facebook\.com\/([a-zA-Z0-9._-]+)\/?$/);
      return match ? match[1] : null;
    },
  },
  {
    id: "instagram",
    name: "Instagram",
    displayName: "Instagram",
    icon: Instagram,
    color: "text-[#E4405F]",
    urlPattern: /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9._]+\/?$/,
    urlPlaceholder: "https://instagram.com/yourhandle",
    extractUsername: (url) => {
      const match = url.match(/instagram\.com\/([a-zA-Z0-9._]+)\/?$/);
      return match ? `@${match[1]}` : null;
    },
  },
  {
    id: "tiktok",
    name: "TikTok",
    displayName: "TikTok",
    icon: TikTokIcon,
    color: "text-foreground",
    urlPattern: /^https?:\/\/(www\.)?tiktok\.com\/@[a-zA-Z0-9._]+\/?$/,
    urlPlaceholder: "https://tiktok.com/@yourhandle",
    extractUsername: (url) => {
      const match = url.match(/tiktok\.com\/(@[a-zA-Z0-9._]+)\/?$/);
      return match ? match[1] : null;
    },
  },
  {
    id: "youtube",
    name: "YouTube",
    displayName: "YouTube",
    icon: Youtube,
    color: "text-[#FF0000]",
    urlPattern: /^https?:\/\/(www\.)?youtube\.com\/(@?[a-zA-Z0-9._-]+|channel\/[a-zA-Z0-9_-]+)\/?$/,
    urlPlaceholder: "https://youtube.com/@yourchannel",
    extractUsername: (url) => {
      const match = url.match(/youtube\.com\/(@?[a-zA-Z0-9._-]+|channel\/[a-zA-Z0-9_-]+)\/?$/);
      if (match) {
        const username = match[1];
        return username.startsWith("@") ? username : `@${username}`;
      }
      return null;
    },
  },
  {
    id: "twitter",
    name: "Twitter",
    displayName: "Twitter / X",
    icon: XIcon,
    color: "text-foreground",
    urlPattern: /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[a-zA-Z0-9_]+\/?$/,
    urlPlaceholder: "https://x.com/yourhandle",
    extractUsername: (url) => {
      const match = url.match(/(twitter\.com|x\.com)\/([a-zA-Z0-9_]+)\/?$/);
      return match ? `@${match[2]}` : null;
    },
  },
  {
    id: "spotify",
    name: "Spotify",
    displayName: "Spotify",
    icon: SpotifyIcon,
    color: "text-[#1ED760]",
    urlPattern: /^https?:\/\/(open\.)?spotify\.com\/.+/,
    urlPlaceholder: "https://open.spotify.com/show/...",
    extractUsername: () => null,
  },
  {
    id: "apple",
    name: "Apple Podcasts",
    displayName: "Apple Podcasts",
    icon: ApplePodcastsIcon,
    color: "text-[#9933CC]",
    urlPattern: /^https?:\/\/podcasts\.apple\.com\/.+/,
    urlPlaceholder: "https://podcasts.apple.com/...",
    extractUsername: () => null,
  },
];

/** Look up a platform config by its ID */
export function getSocialPlatform(id: string): SocialPlatformConfig | undefined {
  return SOCIAL_PLATFORMS.find((p) => p.id === id);
}

// ============================================================================
// Per-show social account mapping
// Maps the 7 nullable show.{platform}_url columns into the same shape the
// public ShowPage <SocialLinksRow> already renders.
// ============================================================================

interface ShowSocialAccount {
  id: string;
  platform: SocialPlatformId;
  profile_url: string;
  platform_username: string | null;
}

type ShowSocialFields = Pick<
  Show,
  | "id"
  | "youtubeUrl"
  | "facebookUrl"
  | "instagramUrl"
  | "xUrl"
  | "tiktokUrl"
  | "spotifyUrl"
  | "appleUrl"
>;

export function buildShowSocialAccounts(show: ShowSocialFields): ShowSocialAccount[] {
  const map: { platform: SocialPlatformId; url: string | null }[] = [
    { platform: "youtube", url: show.youtubeUrl },
    { platform: "facebook", url: show.facebookUrl },
    { platform: "instagram", url: show.instagramUrl },
    { platform: "twitter", url: show.xUrl },
    { platform: "tiktok", url: show.tiktokUrl },
    { platform: "spotify", url: show.spotifyUrl },
    { platform: "apple", url: show.appleUrl },
  ];

  return map
    .filter((e): e is { platform: SocialPlatformId; url: string } =>
      Boolean(e.url && e.url.trim()),
    )
    .map((e) => ({
      id: `${show.id}-${e.platform}`,
      platform: e.platform,
      profile_url: e.url,
      platform_username: null,
    }));
}

